import React, { useState, useEffect, useRef } from 'react';
import {useNavigate} from 'react-router-dom';
import Contacts from './Components/Contacts.js'
import './StyleComponents/HomeNew.css';
import { 
  CalendarCheck2, CalendarCheck, ChevronRight, Globe, Globe2, School, 
  FileEdit, Rocket, Brain, Award, GraduationCap, ClipboardClock, Timer, RefreshCw, 
  ArrowRight, BarChart3, BookOpenCheck, MapPin, Compass, Bot, Laptop,
  Laptop2, FlaskConical
} from 'lucide-react';

const TenaflyPrepHome = () => {
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const contactRef = useRef()
  const servicesRef = useRef()
  const coursesRef = useRef()
  const navigate = useNavigate();
  
  useEffect(()=>{   
        const options = {
            rootMargin: '0px',
            threshold: 0.1
        }
  
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('visible')
            }
            })
        }, options)
  
        const hiddenElements = document.querySelectorAll('.small-card, .contacts-container, .std-testing-trapezoid, .course')
        hiddenElements.forEach((element)=>observer.observe(element))
  
        return () => {
            observer.disconnect()
        }
    },[])

  const scrollTo = (to) => {
    var reference = null
    if (to === "Contact") reference = contactRef
    else if (to === "Courses") reference = coursesRef
    else reference = servicesRef
    const position = reference.current.getBoundingClientRect().top + window.pageYOffset - parseFloat(getComputedStyle(document.documentElement).fontSize)*2.1
    window.scrollTo({top: position, behavior: "smooth"})
  }

  const heroImages = [
    {
      url: "TPA-front.jpg",
      title: "Tenafly Prep Academy",
      subtitle: "Innovative learning strategies for a rapidly changing world."
    },
    {
      url: "stock-classroom-2.jpg",
      title: "Tenafly Prep Academy",
      subtitle: "Innovative learning strategies for a rapidly changing world."
    },
    {
      url: "stock-testing.jpg",
      title: "Tenafly Prep Academy",
      subtitle: "Innovative learning strategies for a rapidly changing world."
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="tp-home-wrapper">
      {/* Hero Carousel */}
      <section className="tp-hero">
        {heroImages.map((hero, idx) => (
          <div 
            key={idx}
            className={`tp-hero-slide ${idx === currentHeroIndex ? 'active' : ''}`}
          >
            <div className="tp-hero-overlay" />
            <img src={hero.url} alt="Campus" className="tp-hero-img" />
            
            <div className="tp-hero-content-wrapper">
              <div className="tp-hero-card">
                <h1 className="tp-hero-title">{hero.title}</h1>
                <p className="tp-hero-subtitle">{hero.subtitle}</p>
                <div className="tp-hero-actions">
                  <button className="tp-btn-primary" onClick={()=>scrollTo("Contact")}>Visit Us</button>
                  <button className="tp-btn-outline" onClick={()=>scrollTo("Services")}>About Us</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Stats Callouts */}
      <section className="tp-stats-section">
        <div className="tp-stats-grid">
          {/* Card 1: AMC Admissions */}
          <div className="tp-stat-card tp-stat-blue" onClick={() => navigate("/amc")}>
            <div className="tp-stat-header">
              <span className="tp-stat-tag">Admissions Open</span>
              <Award size={20} />
            </div>
            <h3 className="tp-stat-title">AMC 10/12 in November</h3>
            <p className="tp-stat-desc">
              We are an official MAA testing center for the upcoming AMC 10/12. Reserve your seat today.
            </p>
            <p>Click for more information <ArrowRight size={18} /></p>
          </div>

          {/* Card 2: Upcoming (no link) */}
          <div className="tp-stat-card tp-stat-teal tp-stat-static">
            <div className="tp-stat-header">
              <span className="tp-stat-tag">Upcoming</span>
              <Rocket size={20} />
            </div>
            <h3 className="tp-stat-title">Digital SAT Self-Study</h3>
            <p className="tp-stat-desc">
              A self-study platform for the Digital SAT is coming soon. Stay tuned!
            </p>
          </div>

          {/* Card 3: unchanged */}
          <div className="tp-stat-card tp-stat-red" onClick={() => navigate("/DigitalTestingIntro")}>
            <div className="tp-stat-header">
              <span className="tp-stat-tag">Service</span>
              <Globe size={20} />
            </div>
            <h3 className="tp-stat-title">Digital Testing Platform</h3>
            <p className="tp-stat-desc">We built our own automated digital testing platform.</p>
            <p>Click to learn more <ArrowRight size={18} /></p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={servicesRef} id="programs" className="tp-services-section">
        <div className="tp-section-header">
          <h2 className="tp-section-title">Our Path to Your Success</h2>
          <div className="tp-section-divider" />
        </div>

        <div className="tp-services-grid">
          {[
            { 
              title: "Standardized Testing", 
              desc: "Data-driven mastery of the SAT, ACT, and AP exams through proven strategies.", 
              icon: BarChart3
            },
            { 
              title: "Enrichment Tutoring", 
              desc: "Targeted support for school subjects and elite competition prep.", 
              icon: BookOpenCheck
            },
            { 
              title: "Official Testing Center", 
              desc: "Authorized host for AMC10/12, Kangaroo Math, and proctored full-length practice exams.", 
              icon: MapPin 
            },
            { 
              title: "Academic Consulting", 
              desc: "Strategic long-term planning to build competitive profiles for elite admissions.", 
              icon: Compass 
            }
          ].map((service, idx) => (
            <div key={idx} className="tp-service-card">
              <div className="tp-service-icon-box">
                <service.icon size={28} />
              </div>
              <h3 className="tp-service-title">{service.title}</h3>
              <p className="tp-service-desc">{service.desc}</p>
              <button className="tp-service-link">
                Learn More <ArrowRight size={18} />
              </button>
            </div>
          ))}

          {/* Alumni Box */}
          <div className="tp-alumni-card">
            <div className="tp-alumni-left">
              <span className="tp-alumni-tag">Where Our Students Go</span>
              <h3 className="tp-alumni-title">Our graduates are accepted into the nation's most prestigious institutions year after year.</h3>
              <div className="tp-alumni-stats">
              </div>
            </div>
            
            <div className="tp-alumni-right">
              {['HARVARD', 'YALE', 'PRINCETON', 'MIT', 'STANFORD', 'COLUMBIA', 'CORNELL', 'UPENN', 'UCHICAGO', 'DUKE', 'BROWN', 'CMU'].map((uni) => (
                <div key={uni} className="tp-uni-badge">{uni}</div>
              ))}
            </div>
            
            <div className="tp-alumni-bg-blob" />
          </div>
        </div>
      </section>

      {/* Learning Cycle */}
      <section className="tp-cycle-section">
        <div className="tp-section-header">
          <h2 className="tp-section-title">Test Prep Methodology</h2>
          <p className="tp-section-desc">
            A simple, proven two-phase process built to turn student effort into academic results.
          </p>
        </div>

        <div className="tp-cycle-container">
          {/* Decorative Background Elements */}
          <div className="tp-cycle-bg-ring" />
          <div className="tp-cycle-connector-line" />

          {/* Central Badge */}
          <div className="tp-cycle-badge">
            <RefreshCw size={32} className="tp-spin" />
            <div className="tp-badge-text">CONTINUOUS<br/>MASTERY</div>
          </div>

          {/* The Two Learning Phases */}
          <div className="tp-cycle-content">
            <div className="tp-cycle-step tp-step-left">
              <div className="tp-cycle-icon-box">
                <School size={24} />
              </div>
              <h3 className="tp-cycle-step-title">1. Lecture + Review</h3>
              <p className="tp-cycle-step-desc">
                Master the core concepts through engaging lessons and expert-guided review sessions.
              </p>
              <div className="tp-taper-line" />
            </div>

            <div className="tp-cycle-step tp-step-right">
              <div className="tp-cycle-icon-box">
                <Timer size={24} />
              </div>
              <h3 className="tp-cycle-step-title">2. Practice Test</h3>
              <p className="tp-cycle-step-desc">
                Take realistic, timed exams to identify gaps and build test-day confidence.
              </p>
              <div className="tp-taper-line" />
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Programs Section */}
      <section ref={coursesRef} className="tp-specialized-section">
        <div className="tp-section-header">
          <h2 className="tp-section-title">Specialized Programs</h2>
          <p className="tp-section-desc">Advanced tracks for students pursuing elite technical and academic distinctions.</p>
        </div>

        <div className="tp-specialized-grid">
          {[
            { 
              title: "Problem Solving (AMC 8/10/12)", 
              desc: "Master high-level mathematics and logic for the American Mathematics Competitions.", 
              icon: Brain,
              color: "gold"
            },
            { 
              title: "Competitive Programming", 
              desc: "Algorithmic thinking and efficient coding to excel in USACO and beyond.", 
              icon: Laptop,
              color: "blue"
            },
            { 
              title: "Columbia SHP Prep", 
              desc: "Targeted preparation for the Columbia University Science Honors Program entrance exam.", 
              icon: FlaskConical,
              color: "red"
            },
            { 
              title: "Robotics", 
              desc: "Hands-on engineering and automation projects focusing on design and mechanics.", 
              icon: Bot,
              color: "teal"
            },
            { 
              title: "Reading & Writing", 
              desc: "Elite humanities training to build sophisticated analysis and communication skills.", 
              icon: FileEdit,
              color: "purple"
            }
          ].map((course, idx) => (
            <div key={idx} className={`tp-special-card tp-border-${course.color}`}>
              <div className="tp-special-icon">
                <course.icon size={24} />
              </div>
              <div className="tp-special-info">
                <h3 className="tp-special-title">{course.title}</h3>
                <p className="tp-special-desc">{course.desc}</p>
              </div>
              <ChevronRight className="tp-special-arrow" size={20} />
            </div>
          ))}
        </div>
      </section>

      <div ref={contactRef}><Contacts/></div>
    </div>
  );
};

export default TenaflyPrepHome;